import { Star } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  HBars,
  LoadingState,
  Select,
  StatTile,
} from '@/components/ui';
import { useBranch } from '@/features/branch/branch-context';
import { useVisitRatings } from '@/features/visit-ratings/api';
import {
  RATING_ASPECTS,
  type RatingAspect,
  type StaffVisitRating,
} from '@/features/visit-ratings/types';
import { dayLabel } from '@/pages/ops/register-format';
import styles from './ServiceRatings.module.css';

const ASPECT_LABEL: Record<RatingAspect, string> = {
  waiting: 'Waiting time',
  cleanliness: 'Cleanliness',
  staff: 'Staff',
  billing: 'Billing',
};

const SHOW_OPTIONS = [
  { value: 'all', label: 'All ratings' },
  { value: 'low', label: 'Low ratings only (1–2 stars)' },
];

/**
 * "Service ratings" (RL-1137): what patients rated after a visit in the app
 * (RL-954), last 90 days, for the selected branch (or all the role's branches
 * with "All"). Service only, never the doctor. Comments are erased when the
 * patient deletes their app account (RL-955); the stars stay.
 */
export function ServiceRatings() {
  const { selected, canSelectAll } = useBranch();
  const [low, setLow] = useState(false);
  const q = useVisitRatings(low);
  const noBranch = !selected && !canSelectAll;
  const scope = selected?.name ?? 'All branches';
  const data = q.data;

  if (noBranch)
    return (
      <Card title="Service ratings">
        <EmptyState
          icon={<Star size={22} />}
          title="Choose a branch to see its service ratings"
        />
      </Card>
    );

  if (!data)
    return q.isLoading || !q.error ? (
      <LoadingState label="Loading the service ratings" />
    ) : (
      <ErrorState error={q.error} onRetry={() => void q.refetch()} />
    );

  return (
    <div className={styles.stack}>
      <div className={styles.stats}>
        <StatTile
          highlight
          label="Rated visits"
          value={data.ratedVisits}
          caption={`Last ${data.periodDays} days · ${scope}`}
        />
        <StatTile
          label="Low ratings"
          value={data.lowRatings}
          caption="A 1- or 2-star answer: offer the patient a call"
        />
      </div>
      <Card title={`Average per aspect · ${scope}`}>
        <p className={styles.note}>
          Patients rate the service only (waiting, cleanliness, staff, billing),
          never the doctor. Out of 5 stars, last {data.periodDays} days.
        </p>
        {data.ratedVisits ? (
          <HBars
            rows={RATING_ASPECTS.map((aspect) => {
              const a = data.aspects.find((x) => x.aspect === aspect);
              const avg = a?.average ?? null;
              return {
                label: ASPECT_LABEL[aspect],
                value: avg ?? 0,
                tone:
                  avg !== null && avg <= 2.5
                    ? ('err' as const)
                    : ('main' as const),
                display:
                  avg === null
                    ? 'Not rated'
                    : `${avg.toFixed(1)} / 5 · ${a!.count} ${a!.count === 1 ? 'rating' : 'ratings'}`,
              };
            })}
          />
        ) : (
          <p className={styles.muted}>
            No ratings in the last {data.periodDays} days.
          </p>
        )}
      </Card>

      <Card
        title="Ratings"
        flush
        actions={
          <div className={styles.filters}>
            <Field label="Show">
              <Select
                value={low ? 'low' : 'all'}
                options={SHOW_OPTIONS}
                onChange={(e) => setLow(e.target.value === 'low')}
              />
            </Field>
          </div>
        }
      >
        <DataTable
          caption="Service ratings from the app"
          rows={data.ratings}
          rowKey={(r) => r.id}
          loading={q.isFetching && q.isPlaceholderData}
          error={q.error}
          onRetry={() => void q.refetch()}
          empty={
            <EmptyState
              icon={<Star size={22} />}
              title={low ? 'No low ratings' : 'No ratings yet'}
              body={
                low
                  ? undefined
                  : 'Patients can rate a finished visit in the app for 14 days.'
              }
            />
          }
          columns={[
            {
              key: 'visit',
              header: 'Visit',
              render: (r) => (
                <>
                  {dayLabel(r.visitDate)}
                  {!selected && (
                    <span className={styles.sub}>{r.branch.name}</span>
                  )}
                </>
              ),
            },
            {
              key: 'patient',
              header: 'Patient',
              render: (r) => (
                <Link to={`/patients/${r.patient.id}`}>
                  {r.patient.fullName}
                </Link>
              ),
            },
            ...RATING_ASPECTS.map((aspect) => ({
              key: aspect,
              header: ASPECT_LABEL[aspect],
              align: 'center' as const,
              render: (r: StaffVisitRating) => (
                <Stars value={r.stars[aspect]} />
              ),
            })),
            {
              key: 'comment',
              header: 'Comment',
              render: (r) =>
                r.comment ? (
                  <span className={styles.comment}>{r.comment}</span>
                ) : (
                  <span className={styles.muted}>No comment</span>
                ),
            },
            {
              key: 'low',
              header: <span className="visually-hidden">Low rating</span>,
              render: (r) =>
                r.low ? <Badge tone="warning">Low rating</Badge> : null,
            },
          ]}
        />
      </Card>
    </div>
  );
}

function Stars({ value }: { value: number | null }) {
  if (value === null) return <span className={styles.muted}>—</span>;
  return (
    <span className={value <= 2 ? styles.lowStars : undefined}>
      {value} / 5{value <= 2 && <span className="visually-hidden"> (low)</span>}
    </span>
  );
}
